import { Link } from 'react-router-dom';
import { LogoutButton } from '@/components/auth/LogoutButton';
import { Mail, BarChart3, Users, Settings, ArrowLeft, Coins, AlertTriangle, DollarSign, Clock, ExternalLink, Shield, Bug, Brain, ShieldAlert } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { EmailTester } from '@/components/admin/EmailTester';
import { EmailEventsLog } from '@/components/admin/EmailEventsLog';
import { CreditPurchasesLog } from '@/components/admin/CreditPurchasesLog';
import { CreditTransactionsLog } from '@/components/admin/CreditTransactionsLog';
import { DangerZone } from '@/components/admin/DangerZone';
import { PricingSettings } from '@/components/admin/PricingSettings';
import { TestClockManager } from '@/components/admin/TestClockManager';
import { TestClockSettings } from '@/components/admin/TestClockSettings';
import { CheckoutSettings } from '@/components/admin/CheckoutSettings';
import { AdminSecuritySettings } from '@/components/admin/AdminSecuritySettings';
import { AIPromptsManager } from '@/components/admin/AIPromptsManager';
import { TestUsersManager } from '@/components/admin/TestUsersManager';
import { UserRolesManager } from '@/components/admin/UserRolesManager';
import { RateLimitMonitor } from '@/components/admin/RateLimitMonitor';

export default function Admin() {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50/30 via-white to-blue-50/30 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 relative overflow-hidden">
      {/* Animated Background Blobs */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-blob pointer-events-none" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-blob animation-delay-2000 pointer-events-none" />
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle, rgba(168, 85, 247, 0.05) 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }} />

      <main className="flex-1 py-8 relative z-10">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 flex items-center justify-center animate-float">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gradient mb-2">Admin Dashboard</h1>
                <p className="text-muted-foreground">Management and Testing Tools</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LogoutButton />
              <Button asChild className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                <Link to="/settings" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Settings
                </Link>
              </Button>
            </div>
          </div>

          <Tabs defaultValue="email-testing" className="space-y-6">
        <TabsList className="flex flex-wrap w-full gap-2 bg-white/5 backdrop-blur-sm p-2 rounded-lg">
          <TabsTrigger value="email-testing">
            <span className="hidden sm:inline">Email Testing</span>
          </TabsTrigger>
          <TabsTrigger value="email-log">
            <span className="hidden sm:inline">Email Log</span>
          </TabsTrigger>
          <TabsTrigger value="credits">
            <span className="hidden sm:inline">Credits</span>
          </TabsTrigger>
          <TabsTrigger value="pricing">
            <span className="hidden sm:inline">Pricing</span>
          </TabsTrigger>
          <TabsTrigger value="test-clock">
            <span className="hidden sm:inline">Test Clock</span>
          </TabsTrigger>
          <TabsTrigger value="test-clock-manager">
            <span className="hidden sm:inline">Test Clock Manager</span>
          </TabsTrigger>
          <TabsTrigger value="checkout">
            <span className="hidden sm:inline">Checkout</span>
          </TabsTrigger>
          <TabsTrigger value="security">
            <span className="hidden sm:inline">Security</span>
          </TabsTrigger>
          <TabsTrigger value="ai-prompts">
            <Brain className="h-4 w-4 sm:hidden" />
            <span className="hidden sm:inline">AI Prompts</span>
          </TabsTrigger>
          <TabsTrigger value="rate-limits">
            <ShieldAlert className="h-4 w-4 sm:hidden" />
            <span className="hidden sm:inline">Rate Limits</span>
          </TabsTrigger>
          <TabsTrigger value="danger" className="text-destructive data-[state=active]:text-destructive">
            <span className="hidden sm:inline">Danger</span>
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="h-4 w-4 sm:hidden" />
            <span className="hidden sm:inline">Test Users</span>
          </TabsTrigger>
          <TabsTrigger value="user-roles">
            <Shield className="h-4 w-4 sm:hidden" />
            <span className="hidden sm:inline">User Roles</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email-testing">
          <EmailTester />
        </TabsContent>

        <TabsContent value="email-log">
          <EmailEventsLog />
        </TabsContent>

        <TabsContent value="credits" className="space-y-6">
          <CreditPurchasesLog />
          <CreditTransactionsLog />
        </TabsContent>

        <TabsContent value="pricing">
          <PricingSettings />
        </TabsContent>

        <TabsContent value="test-clock">
          <TestClockSettings />
        </TabsContent>

        <TabsContent value="test-clock-manager">
          <TestClockManager />
        </TabsContent>

          <TabsContent value="checkout">
            <CheckoutSettings />
          </TabsContent>
          
        <TabsContent value="security">
          <AdminSecuritySettings />
        </TabsContent>

        <TabsContent value="ai-prompts">
          <AIPromptsManager />
        </TabsContent>

        <TabsContent value="rate-limits">
          <RateLimitMonitor />
        </TabsContent>

        <TabsContent value="danger">
          <DangerZone />
        </TabsContent>

        <TabsContent value="users">
          <TestUsersManager />
        </TabsContent>

        <TabsContent value="user-roles">
          <UserRolesManager />
        </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
